<template>
  <div class="vivo">
    <!-- header -->
    <div class="row" style="margin-bottom: 14px">
      <div>
        <div class="pagetitle">{{ $t("employee-import.title") }}</div>
        <div class="crumb">
          {{ $t("common.company") }} / {{ $t("employee-import.crumbEmployees") }} /
          {{ $t("employee-import.crumbImport") }}
        </div>
      </div>
      <div class="searchbar grow">🔎 {{ $t("employee-import.search") }}</div>
      <button class="btn ghost" type="button" @click="goEmployees">
        {{ $t("employee-import.cancel") }}
      </button>
    </div>

    <!-- stepper -->
    <div class="stepper">
      <div class="step" :class="stepClass('download')"><span class="b">1</span> {{ $t("employee-import.steps.download") }}</div>
      <span class="step__line"></span>
      <div class="step" :class="stepClass('upload')"><span class="b">2</span> {{ $t("employee-import.steps.upload") }}</div>
      <span class="step__line"></span>
      <div class="step" :class="stepClass('preview')"><span class="b">3</span> {{ $t("employee-import.steps.preview") }}</div>
      <span class="step__line"></span>
      <div class="step" :class="stepClass('confirm')"><span class="b">4</span> {{ $t("employee-import.steps.confirm") }}</div>
    </div>

    <!-- upload + template -->
    <div class="grid2">
      <div>
        <!-- Real multipart upload: a hidden file input the dropzone triggers. The
             axios seam passes a FormData straight through (no forced JSON), so the
             browser sets multipart/form-data + boundary automatically. -->
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          style="display: none"
          @change="onFileChange"
        />
        <div class="upload" style="cursor: pointer" role="button" tabindex="0" @click="pickFile" @keyup.enter="pickFile">
          <div style="font-size: 22px">📎</div>
          <div>{{ importing ? $t("employee-import.reading") : $t("employee-import.dropzone") }}</div>
          <span class="xs muted">{{ $t("employee-import.dropzoneHint") }}</span>
          <div v-if="fileName" class="xs mt">{{ $t("employee-import.selectedFile", { name: fileName }) }}</div>
        </div>
        <div class="xs muted mt">🪪 {{ $t("employee-import.nationalIdRule") }}</div>
        <div v-if="uploadError" class="note danger mt">{{ uploadError }}</div>
      </div>
      <div class="panel">
        <div class="panel__body">
          <div class="row between">
            <b class="sm">{{ $t("employee-import.templateTitle") }}</b>
            <span class="tag paid">📄 {{ $t("employee-import.templateFile") }}</span>
          </div>
          <p class="xs muted mt">{{ $t("employee-import.templateColumns") }}</p>
          <div class="xs mt"><b>{{ $t("employee-import.templateInstructionsTitle") }}</b></div>
          <ul class="xs muted" style="margin: 4px 0 0; padding-inline-start: 18px">
            <li v-for="i in instructionIdx" :key="i">{{ $t("employee-import.templateInstructions[" + i + "]") }}</li>
          </ul>
          <button class="btn soft sm mt" type="button" @click="downloadTemplate">
            ⬇️ {{ $t("employee-import.downloadTemplate") }}
          </button>
          <div v-if="templateReady" class="note info mt">{{ $t("employee-import.templateReady") }}</div>
        </div>
      </div>
    </div>

    <!-- success -->
    <div v-if="confirmed" class="callout mt">
      <b>✅ {{ $t("employee-import.successTitle") }}</b>
      {{ $t("employee-import.successMsg", { created: fInt(createdCount) }) }}
      <span v-if="updatedCount">{{ $t("employee-import.successUpdated", { updated: fInt(updatedCount) }) }}</span>
      <div v-if="credentials.length" class="mt">
        <b class="sm">{{ $t("employee-import.credentialsTitle") }}</b>
        <div class="xs muted">{{ $t("employee-import.credentialsHint") }}</div>
        <div class="table-wrap mt">
          <table class="table">
            <thead>
              <tr>
                <th>{{ $t("employee-import.th.email") }}</th>
                <th>{{ $t("employee-import.th.password") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(c, i) in credentials" :key="'cred' + i">
                <td>{{ c.email }}</td>
                <td class="mono">{{ c.password }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-if="warnings.length" class="mt">
        <b class="sm">{{ $t("employee-import.warningsTitle") }}</b>
        <ul class="xs" style="margin: 4px 0 0; padding-inline-start: 18px">
          <li v-for="(w, i) in warnings" :key="'w' + i">
            {{ $t("employee-import.skippedRow", { row: w.row, reason: $t("employee-import.rowErrors." + w.code) }) }}
          </li>
        </ul>
      </div>
      <div v-if="skipped.length" class="mt">
        <b class="sm">{{ $t("employee-import.skippedTitle") }}</b>
        <ul class="xs" style="margin: 4px 0 0; padding-inline-start: 18px">
          <li v-for="(s, i) in skipped" :key="i">
            {{ $t("employee-import.skippedRow", { row: s.row, reason: $t("employee-import.rowErrors." + s.code) }) }}
          </li>
        </ul>
      </div>
      <div class="mt">
        <button class="btn soft sm" type="button" @click="reset">{{ $t("employee-import.reimport") }}</button>
      </div>
    </div>

    <!-- file-level warnings (e.g. this looks like a utilization/claims file) -->
    <div v-if="fileWarnings.length" class="note info mt">
      <b class="sm">⚠ {{ $t("employee-import.fileWarningsTitle") }}</b>
      <ul class="xs" style="margin: 4px 0 0; padding-inline-start: 18px">
        <li v-for="(w, i) in fileWarnings" :key="'fw' + i">{{ w }}</li>
      </ul>
    </div>

    <!-- column mapping -->
    <div v-if="mapRows.length" class="panel mt">
      <div class="panel__head">
        <h4>🧭 {{ $t("employee-import.mappingTitle") }}</h4>
        <span class="muted xs">{{ $t("employee-import.mappingHintEditable") }}</span>
      </div>
      <div class="panel__body">
        <div v-for="(m, i) in mapRows" :key="i" class="map-row">
          <div class="src">{{ m.src }}</div>
          <div class="arr">→</div>
          <div class="dst">
            <select v-model="m.dst" class="map-select">
              <option v-for="o in fieldOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
            </select>
          </div>
        </div>
        <div class="mt">
          <button class="btn soft sm" type="button" :disabled="importing" @click="remap">
            {{ importing ? $t("employee-import.remapping") : $t("employee-import.applyMapping") }}
          </button>
        </div>
      </div>
    </div>

    <!-- preview -->
    <div v-if="result" class="panel mt">
      <div class="panel__head">
        <h4>👁️ {{ $t("employee-import.previewTitle") }}<span v-if="fileName">: <span class="mono">{{ fileName }}</span></span></h4>
        <span class="tag ok" style="margin-inline-start: auto">{{ $t("employee-import.validCount", { n: fInt(counts.valid) }) }}</span>
        &nbsp;
        <span class="tag danger">{{ $t("employee-import.invalidCount", { n: fInt(counts.invalid) }) }}</span>
      </div>
      <div class="panel__body p0">
        <div v-if="counts.total > previewRows.length && previewRows.length" class="xs muted" style="padding: 8px 15px">{{ $t("employee-import.showingFirst", { n: fInt(previewRows.length) }) }}</div>
        <div v-if="!previewRows.length" class="state">{{ $t("employee-import.empty") }}</div>
        <div v-else class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>{{ $t("employee-import.th.index") }}</th>
                <th>{{ $t("employee-import.th.name") }}</th>
                <th>{{ $t("employee-import.th.nationalId") }}</th>
                <th>{{ $t("employee-import.th.memberCode") }}</th>
                <th>{{ $t("employee-import.th.relation") }}</th>
                <th>{{ $t("employee-import.th.cardNumber") }}</th>
                <th>{{ $t("employee-import.th.mobile") }}</th>
                <th>{{ $t("employee-import.th.email") }}</th>
                <th>{{ $t("employee-import.th.dept") }}</th>
                <th>{{ $t("employee-import.th_policy") }}</th>
                <th>{{ $t("employee-import.th_errors") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in previewRows" :key="row.index" :class="{ 'row-bad': row.errors.length }">
                <td>{{ fInt(row.index) }}</td>
                <td>{{ row.fullName || $t("employee-import.missing") }}</td>
                <td class="mono">{{ row.nationalId || "—" }}</td>
                <td class="mono">{{ row.memberCode || "—" }}</td>
                <td>{{ row.relation ? $t("employee-import.relations." + row.relation) : "—" }}</td>
                <td>{{ row.cardNumber || "—" }}</td>
                <td class="mono">{{ row.mobile || "—" }}</td>
                <td>{{ row.email || "—" }}</td>
                <td>{{ row.department || "—" }}</td>
                <td class="mono">{{ row.policyNumber || "—" }}</td>
                <td>
                  <span v-if="!row.errors.length" class="tag ok">{{ $t("employee-import.rowValid") }}</span>
                  <span v-for="code in row.errors" :key="code" class="tag danger" style="margin-inline-end: 4px">⚠ {{ $t("employee-import.rowErrors." + errKey(code)) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-if="badRows.length" style="padding: 10px 15px; border-top: 1px solid var(--v-line-2)">
        <b class="sm">{{ $t("employee-import.invalidListTitle") }}</b>
        <ul class="xs" style="margin: 4px 0 0; padding-inline-start: 18px">
          <li v-for="(b, i) in badRows" :key="i">
            {{ $t("employee-import.skippedRow", { row: b.row, reason: b.codes.map((c) => $t("employee-import.rowErrors." + errKey(c))).join("، ") }) }}
          </li>
        </ul>
      </div>
      <div class="row between" style="padding: 12px 15px; border-top: 1px solid var(--v-line-2)">
        <span class="muted xs">{{ $t("employee-import.footerHint", { valid: fInt(counts.valid), invalid: fInt(counts.invalid) }) }}</span>
        <div class="row" style="gap: 8px">
          <button class="btn primary sm" type="button" :disabled="importing || !counts.valid" @click="confirmImport">
            {{ importing ? $t("employee-import.confirming") : $t("employee-import.confirmImport", { valid: fInt(counts.valid) }) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiPost } from "../../api/vivo/http";
import { shapePreview, summarizeResult, buildMapRows, validateImportFile, errorKey, importErrorKey, confirmErrors, invalidRows, assignmentWarnings, credentials, mappingFromRows, FIELD_OPTIONS } from "./employee-import-vm";
import { buildTemplateXlsx } from "./xlsx-template";

export default {
  name: "VivoEmployeeImport",
  data() {
    return {
      result: null,
      file: null, // kept so the mapping can be re-applied by re-uploading the same file
      importing: false,
      confirmed: false,
      createdCount: 0,
      updatedCount: 0,
      credentials: [],
      mapRows: [], // editable: [{ src, dst }]
      templateReady: false,
      fileName: "",
      uploadError: "",
      skipped: [],
      warnings: [],
      instructionIdx: [0, 1, 2, 3, 4],
    };
  },
  computed: {
    previewRows() {
      return this.result ? shapePreview(this.result.preview) : [];
    },
    counts() {
      return summarizeResult(this.result);
    },
    badRows() {
      return invalidRows(this.result);
    },
    fileWarnings() {
      const codes = (this.result && this.result.warnings) || [];
      return codes.map((c) => {
        const key = "employee-import.fileWarnings." + c;
        const m = this.$t(key);
        return m === key ? c : m;
      });
    },
    fieldOptions() {
      return [{ value: "", label: this.$t("employee-import.unmapped") }].concat(
        FIELD_OPTIONS.map((f) => ({ value: f, label: this.fieldLabel(f) })),
      );
    },
  },
  methods: {
    pickFile() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    async onFileChange(e) {
      const input = e && e.target;
      const file = input && input.files && input.files[0];
      if (!file) return;
      this.fileName = file.name;
      this.uploadError = "";
      this.confirmed = false;
      this.result = null;
      this.file = file;
      this.mapRows = [];
      this.credentials = [];

      // Client-side check BEFORE the multipart round-trip: same rules the backend
      // enforces (import.service.ts parses .xlsx only via ExcelJS; the upload
      // interceptor + FileValidationPipe cap at 10MB) — see employee-import-vm.js.
      const check = validateImportFile(file);
      if (!check.valid) {
        this.uploadError = this.$t("employee-import.fileErrors." + check.error);
        if (input) input.value = "";
        return;
      }

      this.importing = true;
      try {
        // FormData → axios sets multipart/form-data + boundary; field name `file`
        // matches the backend FileInterceptor('file').
        const fd = new FormData();
        fd.append("file", file);
        this.result = await apiPost("/employees/import", fd);
        this.mapRows = buildMapRows(this.result.detectedColumns, this.result.suggestedMapping).map(
          (m) => ({ src: m.src, dst: m.dst || "" }),
        );
      } catch (err) {
        this.uploadError = this.$t("employee-import.apiErrors." + importErrorKey(err));
      } finally {
        this.importing = false;
        // Reset the input so re-selecting the same file fires @change again.
        if (input) input.value = "";
      }
    },
    // Re-upload the SAME file with the user's edited column mapping. The backend
    // re-analyzes under the override and returns a fresh preview + stagingId, so
    // confirm then inserts the re-mapped rows. The edited mapRows are preserved.
    async remap() {
      if (!this.file) return;
      this.importing = true;
      this.uploadError = "";
      this.confirmed = false;
      try {
        const fd = new FormData();
        fd.append("file", this.file);
        fd.append("mapping", JSON.stringify(mappingFromRows(this.mapRows)));
        this.result = await apiPost("/employees/import", fd);
      } catch (err) {
        this.uploadError = this.$t("employee-import.apiErrors." + importErrorKey(err));
      } finally {
        this.importing = false;
      }
    },
    async confirmImport() {
      if (!this.result || this.result.stagingId == null) return;
      this.importing = true;
      this.uploadError = "";
      try {
        const res = await apiPost("/employees/import/confirm", { stagingId: String(this.result.stagingId) });
        this.createdCount = res && typeof res.imported === "number" ? res.imported : 0;
        this.updatedCount = res && typeof res.updated === "number" ? res.updated : 0;
        this.credentials = credentials(res);
        this.skipped = confirmErrors(res);
        this.warnings = assignmentWarnings(res);
        this.confirmed = true;
      } catch (err) {
        this.uploadError = this.$t("employee-import.apiErrors." + importErrorKey(err));
      } finally {
        this.importing = false;
      }
    },
    reset() {
      this.result = null;
      this.file = null;
      this.confirmed = false;
      this.createdCount = 0;
      this.updatedCount = 0;
      this.credentials = [];
      this.mapRows = [];
      this.skipped = [];
      this.warnings = [];
      this.fileName = "";
      this.uploadError = "";
    },
    errKey(code) {
      return errorKey(code);
    },
    downloadTemplate() {
      if (typeof document === "undefined" || typeof URL === "undefined" || !URL.createObjectURL) return;
      const blob = new Blob([buildTemplateXlsx()], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "employees-template.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      this.templateReady = true;
    },
    goEmployees() {
      if (this.$route.name !== "employees") this.$router.push({ name: "employees" });
    },
    fInt(n) {
      return new Intl.NumberFormat("ar-EG", { numberingSystem: "latn" }).format(n || 0);
    },
    fieldLabel(dst) {
      if (!dst) return this.$t("employee-import.unmapped");
      const key = "employee-import.fields." + dst;
      const label = this.$t(key);
      return label === key ? dst : label;
    },
    stepClass(step) {
      const hasResult = !!this.result;
      if (step === "download") return "done";
      if (step === "upload") return hasResult ? "done" : "now";
      if (step === "preview") {
        if (this.confirmed) return "done";
        return hasResult ? "now" : "";
      }
      if (step === "confirm") return this.confirmed ? "now" : "";
      return "";
    },
  },
};
</script>

<style scoped>
.row-bad td {
  background: var(--v-danger-soft);
}
</style>
